import React from 'react'
import { Trash2 } from 'lucide-react'
import { AppLayout } from '../../components/layout/AppLayout'
import { Button } from '../../components/ui/Button'
import { Input } from '../../components/ui/Input'
import { SlidePanel } from '../../components/ui/SlidePanel'
import { TabBar } from '../../components/ui/TabBar'
import { apiFetch } from '../../lib/api-client'
import { BankTab } from './tabs/BankTab'
import { BrandingTab } from './tabs/BrandingTab'
import { BrugerTab } from './tabs/BrugerTab'
import { OkonomiTab } from './tabs/OkonomiTab'
import { SkabelonerTab } from './tabs/SkabelonerTab'
import { ReguleringsTab } from './tabs/ReguleringsTab'
import { ModulerTab } from './tabs/ModulerTab'
import { BogforingTab } from './tabs/BogforingTab'
import { EgneFelterTab } from './tabs/EgneFelterTab'

// ─── Types ────────────────────────────────────────────────────────────────────

interface Company {
  id: string
  tenant_id: string
  cvr: string
  name: string
  address: string | null
  email: string | null
  phone: string | null
  website: string | null
  postnr: string | null
  by: string | null
  notes: string | null
  // Bank
  bank_reg_nr?: string | null
  bank_account_nr?: string | null
  iban?: string | null
  swift_bic?: string | null
  // Branding
  logo_url?: string | null
  show_logo_contracts?: boolean
  show_logo_inspections?: boolean
  // Regulering
  digital_signature_method?: string | null
  contract_reminder_days?: number
  // Bogføring
  gl_accounts?: Record<string, string> | null
}

interface CompanyContact {
  id: string
  company_id: string
  contact_type: string
  contact_person: string | null
  contact_company: string | null
  email: string | null
  phone: string | null
  notes: string | null
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

const inputWrap: React.CSSProperties = { marginBottom: 14 }

// ─── Edit Stamdata Panel ──────────────────────────────────────────────────────

interface EditStamdataProps {
  company: Company
  onClose: () => void
  onSaved: (updated: Company) => void
}

function EditStamdataPanel({ company, onClose, onSaved }: EditStamdataProps) {
  const [form, setForm] = React.useState({
    name: company.name ?? '',
    email: company.email ?? '',
    phone: company.phone ?? '',
    website: company.website ?? '',
    address: company.address ?? '',
    postnr: company.postnr ?? '',
    by: company.by ?? '',
  })
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.name.trim()) {
      setError('Virksomhedsnavn er påkrævet.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const updated = await apiFetch<Company>(`/api/v1/companies/${company.id}`, {
        method: 'PATCH',
        body: JSON.stringify({
          name: form.name.trim(),
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
          website: form.website.trim() || null,
          address: form.address.trim() || null,
          postnr: form.postnr.trim() || null,
          by: form.by.trim() || null,
        }),
      })
      onSaved(updated)
      onClose()
    } catch {
      setError('Kunne ikke gemme ændringer. Prøv igen.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleSave}>
      <div style={inputWrap}>
        <Input
          label="Virksomhedsnavn"
          value={form.name}
          onChange={(e) => setForm({ ...form, name: e.target.value })}
          autoFocus
        />
      </div>
      <div style={inputWrap}>
        <Input
          label="E-mail"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </div>
      <div style={inputWrap}>
        <Input
          label="Telefon"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </div>
      <div style={inputWrap}>
        <Input
          label="Hjemmeside"
          value={form.website}
          onChange={(e) => setForm({ ...form, website: e.target.value })}
          placeholder="https://example.dk"
        />
      </div>
      <div style={inputWrap}>
        <Input
          label="Adresse"
          value={form.address}
          onChange={(e) => setForm({ ...form, address: e.target.value })}
        />
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 2fr', gap: 12, marginBottom: 14 }}>
        <Input
          label="Postnr"
          value={form.postnr}
          onChange={(e) => setForm({ ...form, postnr: e.target.value })}
          placeholder="2300"
        />
        <Input
          label="By"
          value={form.by}
          onChange={(e) => setForm({ ...form, by: e.target.value })}
          placeholder="København S"
        />
      </div>

      {error && (
        <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" isLoading={saving}>Gem</Button>
        <Button type="button" variant="secondary" onClick={onClose}>Annuller</Button>
      </div>
    </form>
  )
}

// ─── Add Contact Panel ────────────────────────────────────────────────────────

interface AddContactProps {
  companyId: string
  onClose: () => void
  onAdded: (contact: CompanyContact) => void
}

function AddContactPanel({ companyId, onClose, onAdded }: AddContactProps) {
  const [form, setForm] = React.useState({
    contact_type: '',
    contact_person: '',
    contact_company: '',
    email: '',
    phone: '',
  })
  const [saving, setSaving] = React.useState(false)
  const [error, setError] = React.useState<string | null>(null)

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!form.contact_type.trim()) {
      setError('Kontakttype er påkrævet.')
      return
    }
    setSaving(true)
    setError(null)
    try {
      const created = await apiFetch<CompanyContact>(`/api/v1/companies/${companyId}/contacts`, {
        method: 'POST',
        body: JSON.stringify({
          contact_type: form.contact_type.trim(),
          contact_person: form.contact_person.trim() || null,
          contact_company: form.contact_company.trim() || null,
          email: form.email.trim() || null,
          phone: form.phone.trim() || null,
        }),
      })
      onAdded(created)
      onClose()
    } catch {
      setError('Kunne ikke tilføje kontakten. Prøv igen.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <form onSubmit={handleAdd}>
      <div style={inputWrap}>
        <Input
          label="Kontakttype"
          value={form.contact_type}
          onChange={(e) => setForm({ ...form, contact_type: e.target.value })}
          placeholder="Revisor, Bank, Vicevært..."
          autoFocus
        />
      </div>
      <div style={inputWrap}>
        <Input
          label="Kontaktperson"
          value={form.contact_person}
          onChange={(e) => setForm({ ...form, contact_person: e.target.value })}
        />
      </div>
      <div style={inputWrap}>
        <Input
          label="Virksomhed"
          value={form.contact_company}
          onChange={(e) => setForm({ ...form, contact_company: e.target.value })}
        />
      </div>
      <div style={inputWrap}>
        <Input
          label="E-mail"
          type="email"
          value={form.email}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
      </div>
      <div style={inputWrap}>
        <Input
          label="Telefon"
          value={form.phone}
          onChange={(e) => setForm({ ...form, phone: e.target.value })}
        />
      </div>

      {error && (
        <p style={{ fontSize: 12, color: '#dc2626', marginBottom: 12 }}>{error}</p>
      )}

      <div style={{ display: 'flex', gap: 8 }}>
        <Button type="submit" isLoading={saving}>Tilføj</Button>
        <Button type="button" variant="secondary" onClick={onClose}>Annuller</Button>
      </div>
    </form>
  )
}

// ─── Stamdata Section ─────────────────────────────────────────────────────────

function StamdataSection({ company, onUpdated }: { company: Company; onUpdated: (c: Company) => void }) {
  const [editOpen, setEditOpen] = React.useState(false)

  const rows: [string, string | null | undefined][] = [
    ['Virksomhedsnavn', company.name],
    ['CVR-nr.', company.cvr],
    ['Adresse', company.address],
    ['E-mail', company.email],
    ['Telefon', company.phone],
  ]

  return (
    <>
      <div style={{ background: '#fff', border: '1px solid #e8ecf4', borderRadius: 10, marginBottom: 20 }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #e8ecf4',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: '#94a3b8',
          }}>STAMDATA</span>
          <Button size="sm" onClick={() => setEditOpen(true)}>Rediger</Button>
        </div>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <tbody>
            {rows.map(([label, value]) => (
              <tr key={label} style={{ borderBottom: '1px solid #e8ecf4' }}>
                <td style={{ padding: '10px 20px', fontSize: 13, color: '#64748b', width: '40%' }}>{label}</td>
                <td style={{ padding: '10px 20px', fontSize: 13, color: '#0f172a', fontWeight: 500 }}>
                  {value ?? <span style={{ color: '#94a3b8' }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <SlidePanel isOpen={editOpen} onClose={() => setEditOpen(false)} title="Rediger virksomhed">
        <EditStamdataPanel
          company={company}
          onClose={() => setEditOpen(false)}
          onSaved={onUpdated}
        />
      </SlidePanel>
    </>
  )
}

// ─── Contacts Section ─────────────────────────────────────────────────────────

function ContactsSection({ companyId }: { companyId: string }) {
  const [contacts, setContacts] = React.useState<CompanyContact[]>([])
  const [loading, setLoading] = React.useState(true)
  const [addOpen, setAddOpen] = React.useState(false)

  React.useEffect(() => {
    apiFetch<CompanyContact[]>(`/api/v1/companies/${companyId}/contacts`)
      .then(setContacts)
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [companyId])

  const handleDelete = async (contactId: string) => {
    try {
      await apiFetch(`/api/v1/companies/${companyId}/contacts/${contactId}`, { method: 'DELETE' })
      setContacts((prev) => prev.filter((c) => c.id !== contactId))
    } catch {
      // ignore
    }
  }

  return (
    <>
      <div style={{ background: '#fff', border: '1px solid #e8ecf4', borderRadius: 10 }}>
        <div style={{
          padding: '14px 20px', borderBottom: '1px solid #e8ecf4',
          display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <span style={{
            fontSize: 12, fontWeight: 600, letterSpacing: '0.06em',
            textTransform: 'uppercase', color: '#94a3b8',
          }}>KONTAKTER</span>
          <Button size="sm" onClick={() => setAddOpen(true)}>Tilføj kontakt</Button>
        </div>

        {loading && (
          <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>Henter…</div>
        )}

        {!loading && contacts.length === 0 && (
          <div style={{ padding: 32, textAlign: 'center', fontSize: 13, color: '#94a3b8' }}>
            Der er endnu ikke oprettet nogle kontakter.
          </div>
        )}

        {!loading && contacts.length > 0 && (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f8fafc' }}>
                {['Kontakttype', 'Kontaktperson', 'Virksomhed', 'E-mail', 'Telefon', ''].map((h) => (
                  <th
                    key={h}
                    style={{
                      padding: '8px 20px',
                      textAlign: 'left',
                      fontSize: 11,
                      fontWeight: 600,
                      color: '#64748b',
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      borderBottom: '1px solid #e8ecf4',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {contacts.map((contact, i) => (
                <ContactRow
                  key={contact.id}
                  contact={contact}
                  isLast={i === contacts.length - 1}
                  onDelete={() => handleDelete(contact.id)}
                />
              ))}
            </tbody>
          </table>
        )}
      </div>

      <SlidePanel isOpen={addOpen} onClose={() => setAddOpen(false)} title="Tilføj kontakt">
        <AddContactPanel
          companyId={companyId}
          onClose={() => setAddOpen(false)}
          onAdded={(c) => setContacts((prev) => [...prev, c])}
        />
      </SlidePanel>
    </>
  )
}

function ContactRow({
  contact,
  isLast,
  onDelete,
}: {
  contact: CompanyContact
  isLast: boolean
  onDelete: () => void
}) {
  const [hovered, setHovered] = React.useState(false)

  return (
    <tr
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        background: hovered ? '#f8fafc' : 'transparent',
        transition: 'background 0.1s',
        borderBottom: isLast ? 'none' : '1px solid #e8ecf4',
      }}
    >
      <td style={{ padding: '10px 20px', fontWeight: 500, color: '#0f172a' }}>
        {contact.contact_type}
      </td>
      <td style={{ padding: '10px 20px', color: '#64748b' }}>
        {contact.contact_person ?? <span style={{ color: '#94a3b8' }}>—</span>}
      </td>
      <td style={{ padding: '10px 20px', color: '#64748b' }}>
        {contact.contact_company ?? <span style={{ color: '#94a3b8' }}>—</span>}
      </td>
      <td style={{ padding: '10px 20px', color: '#64748b' }}>
        {contact.email ?? <span style={{ color: '#94a3b8' }}>—</span>}
      </td>
      <td style={{ padding: '10px 20px', color: '#64748b' }}>
        {contact.phone ?? <span style={{ color: '#94a3b8' }}>—</span>}
      </td>
      <td style={{ padding: '10px 20px', textAlign: 'right' }}>
        <button
          onClick={onDelete}
          style={{
            background: 'none', border: 'none', cursor: 'pointer',
            color: '#94a3b8', padding: 4, borderRadius: 4,
            display: 'inline-flex', alignItems: 'center',
          }}
          title="Slet kontakt"
        >
          <Trash2 size={13} />
        </button>
      </td>
    </tr>
  )
}

// ─── Notes Sidebar ────────────────────────────────────────────────────────────

function NotesSidebar({ company, onUpdated }: { company: Company; onUpdated: (c: Company) => void }) {
  const [notes, setNotes] = React.useState(company.notes ?? '')
  const [saving, setSaving] = React.useState(false)

  React.useEffect(() => {
    (async () => { setNotes(company.notes ?? '') })()
  }, [company.notes])

  const handleBlur = async () => {
    if (notes === (company.notes ?? '')) return
    setSaving(true)
    try {
      const updated = await apiFetch<Company>(`/api/v1/companies/${company.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ notes: notes || null }),
      })
      onUpdated(updated)
    } catch {
      // ignore — notes are non-critical
    } finally {
      setSaving(false)
    }
  }

  return (
    <div style={{ background: '#fff', border: '1px solid #e8ecf4', borderRadius: 10 }}>
      <div style={{
        padding: '14px 20px', borderBottom: '1px solid #e8ecf4',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
      }}>
        <span style={{
          fontSize: 12, fontWeight: 600, letterSpacing: '0.06em',
          textTransform: 'uppercase', color: '#94a3b8',
        }}>NOTATER</span>
        {saving && (
          <span style={{ fontSize: 11, color: '#94a3b8' }}>Gemmer…</span>
        )}
      </div>
      <div style={{ padding: 16 }}>
        <textarea
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          onBlur={handleBlur}
          placeholder="Notater kan anføres her..."
          style={{
            width: '100%',
            minHeight: 160,
            border: '1px solid #e8ecf4',
            borderRadius: 6,
            padding: 10,
            fontSize: 13,
            color: '#0f172a',
            fontFamily: 'inherit',
            resize: 'vertical',
            outline: 'none',
            boxSizing: 'border-box',
            lineHeight: 1.5,
          }}
        />
      </div>
    </div>
  )
}

// ─── Page Shell ───────────────────────────────────────────────────────────────

const TABS = [
  { key: 'generelt', label: 'Generelt' },
  { key: 'bank', label: 'Bank' },
  { key: 'branding', label: 'Branding' },
  { key: 'brugere', label: 'Brugere' },
  { key: 'okonomi', label: 'Økonomi' },
  { key: 'skabeloner', label: 'Skabeloner' },
  { key: 'regulering', label: 'Regulering' },
  { key: 'moduler', label: 'Moduler' },
  { key: 'bogforing', label: 'Bogføring' },
  { key: 'egnefelter', label: 'Egne felter' },
]

export function CompanyDetailPage() {
  const [company, setCompany] = React.useState<Company | null>(null)
  const [loading, setLoading] = React.useState(true)
  const [notFound, setNotFound] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState('generelt')

  React.useEffect(() => {
    apiFetch<Company[]>('/api/v1/companies')
      .then((list) => {
        const first = list[0]
        if (!first) {
          setNotFound(true)
        } else {
          setCompany(first)
        }
      })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false))
  }, [])

  if (loading) {
    return (
      <AppLayout pageTitle="Virksomhed">
        <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8', fontSize: 13 }}>Henter…</div>
      </AppLayout>
    )
  }

  if (notFound || !company) {
    return (
      <AppLayout pageTitle="Virksomhed">
        <div style={{ padding: 48, textAlign: 'center' }}>
          <p style={{ fontSize: 13, color: '#64748b' }}>Ingen virksomhed fundet. Opret en virksomhed for at komme i gang.</p>
        </div>
      </AppLayout>
    )
  }

  return (
    <AppLayout pageTitle={company.name}>
      {/* Title row */}
      <div style={{ marginBottom: 0 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, color: '#0f172a', margin: 0 }}>
          {company.name}
        </h1>
        <div style={{ fontSize: 13, color: '#94a3b8', marginTop: 4 }}>CVR: {company.cvr}</div>
      </div>

      {/* Tab bar — sticky below topbar */}
      <div style={{ margin: '20px -28px 0', position: 'sticky', top: 56, zIndex: 5, background: '#f7f8fa' }}>
        <TabBar tabs={TABS} active={activeTab} onChange={setActiveTab} />
      </div>

      {/* Tab content */}
      <div style={{ marginTop: 24 }}>
        {activeTab === 'generelt' && (
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 280px', gap: 20, alignItems: 'start' }}>
            <div>
              <StamdataSection company={company} onUpdated={setCompany} />
              <ContactsSection companyId={company.id} />
            </div>
            <NotesSidebar company={company} onUpdated={setCompany} />
          </div>
        )}
        {activeTab === 'bank' && (
          <BankTab
            companyId={company.id}
            initial={{
              bank_reg_nr: company.bank_reg_nr ?? null,
              bank_account_nr: company.bank_account_nr ?? null,
              iban: company.iban ?? null,
              swift_bic: company.swift_bic ?? null,
            }}
            onSaved={(updated) => setCompany((prev) => prev ? { ...prev, ...updated } : prev)}
          />
        )}
        {activeTab === 'branding' && (
          <BrandingTab
            companyId={company.id}
            initial={{
              logo_url: company.logo_url ?? null,
              show_logo_contracts: company.show_logo_contracts ?? false,
              show_logo_inspections: company.show_logo_inspections ?? false,
            }}
            onSaved={(updated) => setCompany((prev) => prev ? { ...prev, ...updated } : prev)}
          />
        )}
        {activeTab === 'brugere' && <BrugerTab />}
        {activeTab === 'okonomi' && <OkonomiTab />}
        {activeTab === 'skabeloner' && <SkabelonerTab />}
        {activeTab === 'regulering' && <ReguleringsTab companyId={company.id} />}
        {activeTab === 'moduler' && <ModulerTab />}
        {activeTab === 'egnefelter' && <EgneFelterTab companyId={company.id} />}
        {activeTab === 'bogforing' && (
          <BogforingTab
            companyId={company.id}
            initial={company.gl_accounts ?? null}
            onSaved={(updated) => setCompany((prev) => prev ? { ...prev, gl_accounts: updated } : prev)}
          />
        )}
      </div>
    </AppLayout>
  )
}
